import { defineStore } from 'pinia'
import { LabCustomerUserPrivate } from '@team-milva/lab-models/user'
import type {
  ILabCustomerUserBusiness,
  ILabCustomerUserPrivate,
} from '@team-milva/lab-models/user'
import { LabCustomerUserBusiness } from '@/globals/typescript/models/LabCustomerUserBusiness'
import { LabCustomerUserInsurance } from '@/globals/typescript/models/LabCustomerUserInsurance'
import {
  LabCustomerUserBusinessInvite,
  LabCustomerUserInsuranceInvite,
  type ILabCustomerUserBusinessInvite,
  type ILabCustomerUserInsuranceInvite,
} from '@team-milva/lab-models/invite'
import type { ICustomerRef } from '@team-milva/lab-models/customer'
import { customerKey } from '@/globals/typescript/utils/customerOwner'
import type { TLabCustomerUserBusinessRole } from '@team-milva/lab-models/user'
import {
  collection,
  onSnapshot,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import { customerApi } from '@/globals/typescript/utils/api/customerClient'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import { orderBy } from 'lodash-es'
import {
  CustomerPrivatePrice,
  type ICustomerPrivatePrice,
  type ICustomerPrivatePriceAnalysisEntry,
  type ICustomerPrivatePriceTatEntry,
  type ICustomerPrivatePriceAddonEntry,
} from '@team-milva/lab-models/customerUser'
import type { TCustomerAccountPriceDealEntry } from '@team-milva/lab-models/customerAccount'
import type { TCustomerUserImportRow } from '@/globals/typescript/utils/customerUserImport'

type CreateCustomerUserParams = {
  firstName: string
  lastName: string
  email: string
  phone: ILabCustomerUserBusiness['phone']
  mainRole?: ILabCustomerUserBusiness['mainRole']
}

type CreateCustomerUserResult = {
  success: boolean
  code?: string
  id?: string
  welcomeEmailFailed?: boolean
}

type ImportCustomerUsersOutcome = {
  rowNumber: number
  email: string
  status: 'created' | 'already-exists' | 'invalid'
  reason?: string
  id?: string
  /** Created, but the welcome mail could not be sent. */
  welcomeEmailFailed?: boolean
}

/**
 * The resolved shape is success-only. A failed request throws, per the apiClient convention in
 * `CLAUDE.md` — the client never throws on its own, so a store action that returned
 * `{ success: false }` created a second error contract a caller could silently continue past,
 * and the failure reached neither Sentry nor Slack.
 */
type ImportCustomerUsersResult = {
  totalCount: number
  createdCount: number
  outcomes: ImportCustomerUsersOutcome[]
}

type UpdateCustomerUserParams = {
  firstName?: string
  lastName?: string
  phone?: ILabCustomerUserBusiness['phone']
  activeState?: 'active' | 'inactive'
  mainRole?: ILabCustomerUserBusiness['mainRole']
}

type CreateCustomerUserPrivateParams = {
  firstName: string
  lastName: string
  email: string
  phone?: ILabCustomerUserPrivate['phone']
  currencyCode: TLabManagementCurrencyCode
  pricingSegmentID: string
}

type UpdateCustomerUserPrivateParams = {
  firstName?: string
  lastName?: string
  phone?: ILabCustomerUserPrivate['phone']
  languageCode?: ILabCustomerUserPrivate['languageCode']
  invoicing?: ILabCustomerUserPrivate['invoicing']
}

type SaveCustomerPrivatePriceParams = {
  pricingSegmentID: string | null
  currencyCode: ICustomerPrivatePrice['currencyCode']
  analyses: ICustomerPrivatePriceAnalysisEntry[]
  tats: ICustomerPrivatePriceTatEntry[]
  addons: ICustomerPrivatePriceAddonEntry[]
  deals: TCustomerAccountPriceDealEntry[]
}

type TCustomerUser =
  | LabCustomerUserBusiness
  | LabCustomerUserInsurance
  | LabCustomerUserPrivate

/** A user of a business or an insurance customer — the two kinds with an account of their own (MIL-402). */
export type TCompanyCustomerUser = LabCustomerUserBusiness | LabCustomerUserInsurance

export type TCustomerUserInvite =
  | LabCustomerUserBusinessInvite
  | LabCustomerUserInsuranceInvite

// Read through the class: an invite written before MIL-402's backfill has no `customer` yet, and
// the class derives it from `customerAccountID`. An insurance invite must not be built as a
// business one — the class field would overwrite its `type`.
function toCustomerUserInvite(
  data: ILabCustomerUserBusinessInvite | ILabCustomerUserInsuranceInvite
): TCustomerUserInvite {
  if (data.type === 'lab-customer-user-insurance-invite') {
    return new LabCustomerUserInsuranceInvite(data)
  }
  return new LabCustomerUserBusinessInvite(data)
}

/**
 * The customer-app API is mounted twice (MIL-402): under `customer-accounts/{id}` for a business
 * customer and `insurance-customer-accounts/{id}` for an insurance one. A private customer has no
 * account and so no users or invites of its own — calling with one is a programming error.
 */
function assertCompanyCustomer(customer: ICustomerRef): void {
  if (customer.type !== 'business' && customer.type !== 'insurance') {
    throw new Error('invalid-params')
  }
}

// The model class for a `Apps/-lab-customer-/Users` document, by its `type`. Before MIL-402 every
// non-private user was business; an insurance user built as `LabCustomerUserBusiness` would have
// had its type overwritten to business by the class field.
function toCustomerUser(data: DocumentData): TCustomerUser {
  if (data.type === 'lab-customer-user-private') {
    return new LabCustomerUserPrivate(data)
  }
  if (data.type === 'lab-customer-user-insurance') {
    return new LabCustomerUserInsurance(data)
  }
  return new LabCustomerUserBusiness(data)
}

export const useCustomerUserStore = defineStore('customerUser', {
  state: () => ({
    customerUsers: [] as TCustomerUser[],
    unsubscribeCustomerUsers: null as (() => void) | null,
    customerUserInvites: [] as TCustomerUserInvite[],
    unsubscribeCustomerUserInvites: null as (() => void) | null,
    allCustomerPrivatePrices: [] as CustomerPrivatePrice[],
    unsubscribeAllCustomerPrivatePrices: null as (() => void) | null,
    privateSearchText: '' as string,
    roleFilter: '' as TLabCustomerUserBusinessRole | '',
  }),

  getters: {
    customerUsersLoaded: (state): boolean => !!state.unsubscribeCustomerUsers,
    customerUserInvitesLoaded: (state): boolean =>
      !!state.unsubscribeCustomerUserInvites,
    allCustomerPrivatePricesLoaded: (state): boolean =>
      !!state.unsubscribeAllCustomerPrivatePrices,

    customerUsersBusiness: (state): LabCustomerUserBusiness[] =>
      state.customerUsers.filter(
        (x): x is LabCustomerUserBusiness =>
          x.type === 'lab-customer-user-business'
      ),

    // MIL-402 — insurance customers' users. Listed separately so the business lists and
    // groupings above never pick them up; the insurance Users tab arrives in Phase 5.
    customerUsersInsurance: (state): LabCustomerUserInsurance[] =>
      state.customerUsers.filter(
        (x): x is LabCustomerUserInsurance =>
          x.type === 'lab-customer-user-insurance'
      ),

    customerUsersPrivate: (state): LabCustomerUserPrivate[] =>
      state.customerUsers.filter(
        (x): x is LabCustomerUserPrivate =>
          x.type === 'lab-customer-user-private'
      ),

    // Business users only, keyed by `CustomerAccount` ID — what every business-only list and
    // picker reads (create order, seed orders, contact person, the customer list's user count).
    customerUsersByAccountID(): Record<string, LabCustomerUserBusiness[]> {
      const result: Record<string, LabCustomerUserBusiness[]> = {}
      for (const user of this.customerUsersBusiness) {
        if (user.customer.type !== 'business') continue
        const users = result[user.customer.id] ?? []
        users.push(user)
        result[user.customer.id] = users
      }
      return result
    },

    // Business and insurance users, keyed by `customerKey(customer)` (MIL-402) — what the shared
    // Brugere tab reads. An ID alone is not unique across the two account collections.
    customerUsersByCustomerKey(): Record<string, TCompanyCustomerUser[]> {
      const result: Record<string, TCompanyCustomerUser[]> = {}
      for (const user of [
        ...this.customerUsersBusiness,
        ...this.customerUsersInsurance,
      ]) {
        const key = customerKey(user.customer)
        const users = result[key] ?? []
        users.push(user)
        result[key] = users
      }
      return result
    },

    customerUserInvitesByCustomerKey: (
      state
    ): Record<string, TCustomerUserInvite[]> => {
      const result: Record<string, TCustomerUserInvite[]> = {}
      for (const invite of state.customerUserInvites) {
        const key = customerKey(invite.customer)
        const invites = result[key] ?? []
        invites.push(invite)
        result[key] = invites
      }
      return result
    },
  },

  actions: {
    getCustomerUsers(): Promise<TCustomerUser[]> {
      if (this.customerUsersLoaded) return Promise.resolve(this.customerUsers)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Users'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.customerUsers = querySnapshot.docs.map((doc) =>
            toCustomerUser(doc.data())
          )

          if (!resolved) {
            resolved = true
            resolve(this.customerUsers)
          }
        })

        this.unsubscribeCustomerUsers = unsubscribe
      })
    },

    getCustomerUserInvites(): Promise<TCustomerUserInvite[]> {
      if (this.customerUserInvitesLoaded)
        return Promise.resolve(this.customerUserInvites)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Invites'),
          where('labAccountID', '==', accountStore.labAccount?.id),
          where('status', '==', 'pending'),
          where('timestamps.expiresAt', '>', Date.now())
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.customerUserInvites = orderBy(
            querySnapshot.docs.map((doc) =>
              toCustomerUserInvite(
                doc.data() as
                  | ILabCustomerUserBusinessInvite
                  | ILabCustomerUserInsuranceInvite
              )
            ),
            ['email', 'timestamps.createdAt'],
            ['asc', 'desc']
          )

          if (!resolved) {
            resolved = true
            resolve(this.customerUserInvites)
          }
        })

        this.unsubscribeCustomerUserInvites = unsubscribe
      })
    },

    async inviteCustomerUsers(
      customer: ICustomerRef,
      emails: string[]
    ): Promise<void> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/invites',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
                body: { emails },
              }
            )
          : await customerApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/invites',
              {
                path: { labAccountID, customerAccountID: customer.id },
                body: { emails },
              }
            )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * Turns welcome emails on for a business or insurance customer and sends one to every active
     * user who has not had one yet (MIL-397). One-way — a second call is rejected by lab-cloud
     * rather than re-sending.
     */
    async enableCustomerWelcomeEmails(
      customer: ICustomerRef
    ): Promise<{ sent: number; skipped: number; failed: number }> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/users/welcome-emails/enable',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
              }
            )
          : await customerApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/users/welcome-emails/enable',
              { path: { labAccountID, customerAccountID: customer.id } }
            )
      if (!result.success) throw new Error(result.code)

      return result.data
    },

    async cancelCustomerInvite(
      customer: ICustomerRef,
      inviteID: string
    ): Promise<void> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/invites/revoke',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
                body: { inviteID },
              }
            )
          : await customerApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/invites/revoke',
              {
                path: { labAccountID, customerAccountID: customer.id },
                body: { inviteID },
              }
            )
      if (!result.success) throw new Error(result.code)
    },

    /**
     * Unlike the other actions this resolves rather than throws on a failed request: callers
     * branch on `code` — `already-exists` means the email belongs to another user (of any type),
     * which they show as its own message rather than the generic error.
     */
    async createCustomerUser(
      customer: ICustomerRef,
      params: CreateCustomerUserParams
    ): Promise<CreateCustomerUserResult> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      const body = {
        ...params,
        createdByUserID: useUserStore().currentUser?.id ?? '',
      }

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/users',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
                body,
              }
            )
          : await customerApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/users',
              { path: { labAccountID, customerAccountID: customer.id }, body }
            )

      return {
        success: result.success,
        code: result.success ? undefined : result.code,
        id: result.success ? result.data.id : undefined,
        welcomeEmailFailed: result.success
          ? result.data.welcomeEmailFailed
          : undefined,
      }
    },

    /** Business-only shorthand, kept for the order wizard and the contact-person picker. */
    createCustomerUserBusiness(
      customerAccountID: string,
      params: CreateCustomerUserParams
    ): Promise<CreateCustomerUserResult> {
      return this.createCustomerUser(
        { type: 'business', id: customerAccountID },
        params
      )
    },

    async importCustomerUsers(
      customer: ICustomerRef,
      rows: TCustomerUserImportRow[]
    ): Promise<ImportCustomerUsersResult> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      const body = {
        createdByUserID: useUserStore().currentUser?.id ?? '',
        rows,
      }

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'post',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/users/import',
              {
                path: { labAccountID, insuranceCustomerAccountID: customer.id },
                body,
              }
            )
          : await customerApi(
              'post',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/users/import',
              { path: { labAccountID, customerAccountID: customer.id }, body }
            )

      if (!result.success) throw new Error(result.code)

      return {
        totalCount: result.data.totalCount,
        createdCount: result.data.createdCount,
        outcomes: result.data.outcomes,
      }
    },

    async updateCustomerUser(
      customer: ICustomerRef,
      userID: string,
      params: UpdateCustomerUserParams
    ): Promise<void> {
      assertCompanyCustomer(customer)
      const labAccountID = useAccountStore().labAccount?.id ?? ''

      const result =
        customer.type === 'insurance'
          ? await customerApi(
              'patch',
              'lab-accounts/{labAccountID}/insurance-customer-accounts/{insuranceCustomerAccountID}/users/{userID}',
              {
                path: {
                  labAccountID,
                  insuranceCustomerAccountID: customer.id,
                  userID,
                },
                body: params,
              }
            )
          : await customerApi(
              'patch',
              'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/users/{userID}',
              {
                path: { labAccountID, customerAccountID: customer.id, userID },
                body: params,
              }
            )

      if (!result.success) {
        throw new Error(result.code)
      }
    },

    setPrivateSearchText(text: string): void {
      this.privateSearchText = text
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    hydrateFromPublicData(raw: unknown[]): void {
      this.customerUsers = (raw as DocumentData[]).map(toCustomerUser)
    },

    resetStore(): void {
      this.unsubscribeCustomerUsers?.()
      this.customerUsers = []
      this.unsubscribeCustomerUsers = null
      this.unsubscribeCustomerUserInvites?.()
      this.customerUserInvites = []
      this.unsubscribeCustomerUserInvites = null
      this.unsubscribeAllCustomerPrivatePrices?.()
      this.allCustomerPrivatePrices = []
      this.unsubscribeAllCustomerPrivatePrices = null
      this.privateSearchText = ''
      this.roleFilter = ''
    },

    async updateCustomerUserPrivate(
      userID: string,
      params: UpdateCustomerUserPrivateParams
    ) {
      const accountStore = useAccountStore()

      return await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/private-users/{customerUserPrivateID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerUserPrivateID: userID,
          },
          body: params,
        }
      )
    },

    async createCustomerUserPrivate(
      params: CreateCustomerUserPrivateParams
    ): Promise<string> {
      const accountStore = useAccountStore()
      const userStore = useUserStore()

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/private-users',
        {
          path: { labAccountID: accountStore.labAccount?.id ?? '' },
          body: {
            ...params,
            createdByUserID: userStore.currentUser?.id ?? '',
            // TODO: remove cast after deploying and re-running openapi:gen
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            currencyCode: params.currencyCode as any,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id
    },

    getCustomerPrivatePrices(): Promise<CustomerPrivatePrice[]> {
      if (this.allCustomerPrivatePricesLoaded)
        return Promise.resolve(this.allCustomerPrivatePrices)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'CustomerPrivatePrices'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allCustomerPrivatePrices = querySnapshot.docs.map(
            (doc) =>
              new CustomerPrivatePrice(doc.data() as ICustomerPrivatePrice)
          )
          if (!resolved) {
            resolved = true
            resolve(this.allCustomerPrivatePrices)
          }
        })

        this.unsubscribeAllCustomerPrivatePrices = unsubscribe
      })
    },

    async saveCustomerPrivatePrice(
      privateUserID: string,
      params: SaveCustomerPrivatePriceParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      // TODO: remove cast after deploying and re-running openapi:gen —
      // `addons` isn't in the deployed OpenAPI spec's body type yet.
      const body = {
        pricingSegmentID: params.pricingSegmentID,
        currencyCode: params.currencyCode,
        analyses: params.analyses,
        tats: params.tats,
        addons: params.addons,
        deals: params.deals,
      }

      const result = await managementApi(
        'put',
        'lab-accounts/{labAccountID}/private-users/{customerUserPrivateID}/pricing',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerUserPrivateID: privateUserID,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        }
      )
      if (!result.success) throw new Error(result.code)
    },
  },
})
